import React, { useState } from "react";
import { toast } from "sonner";
import { User, KeyRound } from "lucide-react";
import { authApi } from "../../services/authApi";
import { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../hooks/useAuth";
import { useLocalization } from "../../hooks/useLocalization";
import { PageHelpBanner } from "../../components/PageHelpBanner";

export default function MyAccountPage() {
  const { t } = useLocalization();
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [profilePassword, setProfilePassword] = useState("");
  const [profileError, setProfileError] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileError("");
    setProfileSaving(true);
    try {
      await authApi.updateProfile(name, email, profilePassword);
      await refreshUser();
      setProfilePassword("");
      toast.success(t("account.profileUpdated"));
    } catch (err) {
      setProfileError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError("");
    if (newPassword !== confirmPassword) {
      setPasswordError(t("changePassword.mismatch"));
      return;
    }
    setPasswordSaving(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
      toast.success(t("account.passwordUpdated"));
    } catch (err) {
      setPasswordError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="max-w-lg space-y-6" data-testid="my-account-page">
      <PageHelpBanner pageKey="my-account" title="How to use this page">
        <p>Update your own display name, login email, or password here. Two-factor authentication and backup codes live on the separate Security page.</p>
      </PageHelpBanner>
      <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("account.title")}</h2>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <User size={18} className="text-slate-500" />
          <h3 className="font-semibold text-gray-900">{t("account.profileSectionTitle")}</h3>
        </div>
        <form onSubmit={handleProfileSave} className="space-y-3" data-testid="my-account-profile-form">
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">{t("account.nameLabel")}</label>
            <input
              data-testid="my-account-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">{t("account.emailLabel")}</label>
            <input
              data-testid="my-account-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">{t("account.emailIsLoginHint")}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">{t("account.confirmPasswordLabel")}</label>
            <input
              data-testid="my-account-confirm-password-input"
              type="password"
              required
              value={profilePassword}
              onChange={(e) => setProfilePassword(e.target.value)}
              placeholder={t("mfa.currentPasswordLabel")}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {profileError && <p className="text-sm text-red-500" data-testid="my-account-profile-error">{profileError}</p>}
          <button
            data-testid="my-account-save-profile-button"
            type="submit"
            disabled={profileSaving}
            className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50"
          >
            {profileSaving ? t("common.loading") : t("common.save")}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound size={18} className="text-slate-500" />
          <h3 className="font-semibold text-gray-900">{t("changePassword.title")}</h3>
        </div>
        <form onSubmit={handlePasswordChange} className="space-y-3" data-testid="my-account-password-form">
          <input
            data-testid="my-account-current-password-input"
            type="password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder={t("changePassword.currentPassword")}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            data-testid="my-account-new-password-input"
            type="password"
            required
            minLength={8}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder={t("changePassword.newPassword")}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            data-testid="my-account-confirm-new-password-input"
            type="password"
            required
            minLength={8}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t("changePassword.confirmNewPassword")}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {passwordError && <p className="text-sm text-red-500" data-testid="my-account-password-error">{passwordError}</p>}
          <button
            data-testid="my-account-save-password-button"
            type="submit"
            disabled={passwordSaving}
            className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-50"
          >
            {passwordSaving ? t("common.loading") : t("changePassword.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
